import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Bot,
  MessageSquare,
  AlertTriangle,
  HelpCircle,
  Save,
  Plus,
  Trash2,
  Loader2,
  Check,
  Play
} from 'lucide-react';
import axios from 'axios';
import WhatsAppResponseHours from '../../components/support/WhatsAppResponseHours';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIConfig = () => {
  const [clinic, setClinic] = useState(null);
  const [aiConfig, setAiConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  
  // Test conversation
  const [testMessage, setTestMessage] = useState('');
  const [testConversation, setTestConversation] = useState([]);

  useEffect(() => {
    loadClinic();
  }, []);

  const loadClinic = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/clinic/${clinicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClinic(response.data);
      
      // Initialize AI config with defaults
      const config = response.data.ai_config || {
        greeting_message: `¡Hola! 👋 Soy el asistente de ${response.data.name}. ¿En qué puedo ayudarte?`,
        personality: 'professional',
        language: 'es',
        can_schedule_appointments: true,
        can_provide_prices: true,
        can_answer_medical_questions: false,
        emergency_message: 'Esto parece una urgencia. Déjame transferirte con un asesor que pueda ayudarte de inmediato.',
        out_of_hours_message: 'Nuestro horario de atención es de Lunes a Viernes. Te responderemos en cuanto abramos.',
        faqs: [],
        qualification_questions: [
          '¿Es tu primera vez visitándonos?',
          '¿Qué servicio te interesa?'
        ]
      };
      setAiConfig(config);
    } catch (error) {
      console.error('Error loading clinic:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      await axios.patch(`${API}/clinic/${clinicId}`, {
        ai_config: aiConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddFaq = () => {
    if (!newFaq.question || !newFaq.answer) return;
    
    setAiConfig(prev => ({
      ...prev,
      faqs: [...(prev.faqs || []), { ...newFaq }]
    }));
    setNewFaq({ question: '', answer: '' });
  };

  const handleRemoveFaq = (index) => {
    setAiConfig(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  const [testLoading, setTestLoading] = useState(false);
  const [testConversationId] = useState(() => `test-${Date.now()}`);

  const handleTestMessage = async () => {
    if (!testMessage.trim() || testLoading) return;
    
    const userMsg = testMessage;
    setTestMessage('');
    setTestLoading(true);
    
    // Add user message
    setTestConversation(prev => [
      ...prev,
      { role: 'user', content: userMsg }
    ]);
    
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/clinic/${clinicId}/chat`, {
        message: userMsg,
        conversation_id: testConversationId,
        request_id: crypto.randomUUID()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTestConversation(prev => [
        ...prev,
        { role: 'assistant', content: response.data.response }
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setTestConversation(prev => [
        ...prev,
        { role: 'assistant', content: 'Error al conectar con el asistente. Intenta de nuevo.' }
      ]);
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Asistente IA">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Configurar Asistente IA">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <div className="space-y-6">
          <WhatsAppResponseHours key={localStorage.getItem('clinicId')} clinicId={localStorage.getItem('clinicId')} />
          {/* Personality & Messages */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bot className="w-5 h-5 text-blue-400" />
                Personalidad del Asistente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400">Mensaje de bienvenida</Label>
                <Textarea
                  value={aiConfig?.greeting_message || ''}
                  onChange={(e) => setAiConfig({ ...aiConfig, greeting_message: e.target.value })}
                  placeholder="¡Hola! Soy el asistente de..."
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                <p className="text-gray-500 text-xs mt-1">Usa {'{clinic_name}'} para insertar el nombre de la clínica</p>
              </div>
              
              <div>
                <Label className="text-gray-400">Estilo de comunicación</Label>
                <select
                  value={aiConfig?.personality || 'professional'}
                  onChange={(e) => setAiConfig({ ...aiConfig, personality: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 mt-1"
                >
                  <option value="professional">Profesional</option>
                  <option value="friendly">Amigable</option>
                  <option value="formal">Formal</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-400" />
                Permisos del Asistente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Puede agendar citas</p>
                  <p className="text-gray-500 text-sm">El asistente puede ofrecer fechas y confirmar citas</p>
                </div>
                <Switch
                  checked={aiConfig?.can_schedule_appointments}
                  onCheckedChange={(checked) => setAiConfig({ ...aiConfig, can_schedule_appointments: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Puede dar precios</p>
                  <p className="text-gray-500 text-sm">Informará sobre los precios de los servicios</p>
                </div>
                <Switch
                  checked={aiConfig?.can_provide_prices}
                  onCheckedChange={(checked) => setAiConfig({ ...aiConfig, can_provide_prices: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Puede responder preguntas médicas</p>
                  <p className="text-gray-500 text-sm text-yellow-500">⚠️ No recomendado - puede dar información incorrecta</p>
                </div>
                <Switch
                  checked={aiConfig?.can_answer_medical_questions}
                  onCheckedChange={(checked) => setAiConfig({ ...aiConfig, can_answer_medical_questions: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Emergency Message */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Mensaje de Emergencia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={aiConfig?.emergency_message || ''}
                onChange={(e) => setAiConfig({ ...aiConfig, emergency_message: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-gray-500 text-xs mt-1">Se mostrará cuando el paciente mencione urgencia o emergencia</p>
            </CardContent>
          </Card>

          {/* FAQs */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-400" />
                Preguntas Frecuentes Personalizadas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {aiConfig?.faqs?.map((faq, index) => (
                <div key={index} className="p-3 bg-white/5 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">{faq.question}</p>
                      <p className="text-gray-400 text-sm mt-1">{faq.answer}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFaq(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="space-y-2 pt-4 border-t border-white/10">
                <Input
                  placeholder="Pregunta (ej: ¿Aceptan seguro?)"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
                <Textarea
                  placeholder="Respuesta"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
                <Button onClick={handleAddFaq} className="w-full bg-white/10 hover:bg-white/20">
                  <Plus className="w-4 h-4 mr-2" /> Agregar FAQ
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saved ? 'Guardado' : 'Guardar configuración'}
          </Button>
        </div>

        {/* Test Conversation */}
        <div className="lg:sticky lg:top-24 h-fit">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Play className="w-5 h-5 text-green-400" />
                Probar Asistente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-[#0a0a0f] rounded-lg h-96 flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {testConversation.length === 0 && (
                    <p className="text-gray-500 text-center text-sm py-8">
                      Escribe un mensaje para probar cómo responderá tu asistente
                    </p>
                  )}
                  {testConversation.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-white/10 text-white rounded-bl-md'
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Input */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleTestMessage()}
                      placeholder="Escribe un mensaje de prueba..."
                      className="bg-white/5 border-white/10 text-white"
                      disabled={testLoading}
                    />
                    <Button 
                      onClick={handleTestMessage} 
                      disabled={testLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar'}
                    </Button>
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                onClick={() => setTestConversation([])}
                className="w-full mt-2 text-gray-400 hover:text-white"
              >
                Limpiar conversación
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AIConfig;

